import React, { useState, useEffect } from 'react';
import { 
  getCarInventory, 
  saveCarInventory, 
  fetchCarInventoryFromServer, 
  deleteCarPermanently, 
  deleteVariantPermanently, 
  getApiUrl,
  CarItem 
} from '../../utils/carInventoryStorage';
import './Dashboard.css';

interface InventoryStructure {
  brands: string[];
  modelsByBrand: Record<string, string[]>;
  variantsByModel: Record<string, string[]>;
}

const extractStructureFromCars = (carsList: CarItem[]): InventoryStructure => {
  const knownBrands = ['Maruti Suzuki', 'Hyundai', 'Toyota', 'Tata', 'Mahindra', 'Kia', 'Honda'];
  const brandsSet = new Set<string>(knownBrands);
  const modelsByBrand: Record<string, string[]> = {};
  const variantsByModel: Record<string, string[]> = {};

  knownBrands.forEach(b => {
    modelsByBrand[b] = [];
  });

  carsList.forEach(car => {
    let matchedBrand = knownBrands.find(b => car.name.toLowerCase().startsWith(b.toLowerCase()));
    let modelName = car.name;
    if (matchedBrand) {
      modelName = car.name.slice(matchedBrand.length).trim();
    } else {
      const parts = car.name.split(' ');
      matchedBrand = parts[0] || 'Other';
      modelName = parts.slice(1).join(' ') || car.name;
      brandsSet.add(matchedBrand);
    }

    if (!modelsByBrand[matchedBrand]) modelsByBrand[matchedBrand] = [];
    if (!modelsByBrand[matchedBrand].includes(modelName)) {
      modelsByBrand[matchedBrand].push(modelName);
    }

    const key = `${matchedBrand} ${modelName}`.trim();
    const varNames = (car.variants || []).map(v => v.name);
    variantsByModel[key] = varNames;
    variantsByModel[modelName] = varNames;
  });

  return {
    brands: Array.from(brandsSet),
    modelsByBrand,
    variantsByModel
  };
};

const CarsManagement: React.FC = () => {
  const [cars, setCars] = useState<CarItem[]>([]);
  const [isCarModalOpen, setIsCarModalOpen] = useState(false);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [selectedCarForVariant, setSelectedCarForVariant] = useState<CarItem | null>(null);

  // Add Car Form State
  const [quotationCarType, setQuotationCarType] = useState<'t-permit' | 'private'>('t-permit');
  
  // Brand
  const [selectedBrand, setSelectedBrand] = useState('');
  const [isCustomBrand, setIsCustomBrand] = useState(false);
  const [customBrandName, setCustomBrandName] = useState('');

  // Model
  const [selectedModel, setSelectedModel] = useState('');
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [customModelName, setCustomModelName] = useState('');

  // Variant
  const [selectedVariant, setSelectedVariant] = useState('');
  const [isCustomVariant, setIsCustomVariant] = useState(false);
  const [customVariantName, setCustomVariantName] = useState('');

  // Pricing & Extras
  const [newCarColors, setNewCarColors] = useState('White, Silver, Grey');
  const [newCarExShowroom, setNewCarExShowroom] = useState('');
  const [newCarDownPayment, setNewCarDownPayment] = useState('');
  const [newCarEmi, setNewCarEmi] = useState('');
  const [newCarImage, setNewCarImage] = useState('');

  // New Variant to Existing Car Modal State
  const [variantName, setVariantName] = useState('');
  const [variantColors, setVariantColors] = useState('White, Silver, Grey');

  const structure = extractStructureFromCars(cars);
  const availableVariants = selectedModel ? (structure.variantsByModel[`${selectedBrand} ${selectedModel}`] || structure.variantsByModel[selectedModel] || []) : [];

  useEffect(() => {
    loadCars();
    fetchCarInventoryFromServer().then(freshCars => {
      if (Array.isArray(freshCars)) {
        setCars(freshCars);
      }
    });
  }, []);

  const loadCars = () => {
    const list = getCarInventory();
    setCars(list);
  };

  const handleOpenAddCarModal = () => {
    setQuotationCarType('t-permit');
    setSelectedBrand('');
    setIsCustomBrand(false);
    setCustomBrandName('');
    setSelectedModel('');
    setIsCustomModel(false);
    setCustomModelName('');
    setSelectedVariant('');
    setIsCustomVariant(false);
    setCustomVariantName('');
    setNewCarColors('White, Silver, Grey');
    setNewCarExShowroom('');
    setNewCarDownPayment('');
    setNewCarEmi('');
    setNewCarImage('');
    setIsCarModalOpen(true);
  };

  const handleBrandSelect = (brand: string) => {
    setSelectedBrand(brand);
    setIsCustomBrand(false);
    setSelectedModel('');
    setIsCustomModel(false);
    setSelectedVariant('');
    setIsCustomVariant(false);
  };

  const handleModelSelect = (model: string) => {
    setSelectedModel(model);
    setIsCustomModel(false);
    setSelectedVariant('');
    setIsCustomVariant(false);
  };

  const handleOpenAddVariantModal = (car: CarItem) => {
    setSelectedCarForVariant(car);
    setVariantName('');
    setVariantColors('White, Silver, Grey');
    setIsVariantModalOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Try direct server upload first
    try {
      const formData = new FormData();
      formData.append('image', file);
      const apiUrl = getApiUrl();
      const res = await fetch(`${apiUrl}/api/upload/car`, {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          setNewCarImage(data.url);
          return;
        }
      }
    } catch (err) {
      console.warn('Direct upload error, falling back to base64 reader:', err);
    }

    // Fallback: Read as base64
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        setNewCarImage(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  // Add / Upsert Car
  const handleSaveCar = (e: React.FormEvent) => {
    e.preventDefault();

    const finalBrand = (isCustomBrand ? customBrandName : selectedBrand).trim();
    const finalModel = (isCustomModel || isCustomBrand ? customModelName : selectedModel).trim();
    const finalVariant = (isCustomVariant || isCustomModel || isCustomBrand || availableVariants.length === 0 ? customVariantName : selectedVariant).trim();

    if (!finalBrand) {
      alert('Please select or enter a Car Brand');
      return;
    }
    if (!finalModel) {
      alert('Please select or enter a Car Model');
      return;
    }
    if (!finalVariant) {
      alert('Please select or enter at least one Model Variant');
      return;
    }

    let fullCarName = finalModel;
    if (!finalModel.toLowerCase().startsWith(finalBrand.toLowerCase())) {
      fullCarName = `${finalBrand} ${finalModel}`.trim();
    }

    const currentCars = getCarInventory();
    const colorsArray = newCarColors.split(',').map(c => c.trim()).filter(Boolean);
    const existingIndex = currentCars.findIndex(c => c.name.toLowerCase().trim() === fullCarName.toLowerCase().trim());

    let updatedCars: CarItem[] = [];

    if (existingIndex >= 0) {
      const existingCar = currentCars[existingIndex];
      const hasVariant = (existingCar.variants || []).some(v => v.name.toLowerCase().trim() === finalVariant.toLowerCase().trim());
      const updatedVariants = hasVariant 
        ? existingCar.variants 
        : [...(existingCar.variants || []), { name: finalVariant, colors: colorsArray.length > 0 ? colorsArray : ['White'] }];

      currentCars[existingIndex] = {
        ...existingCar,
        carType: quotationCarType,
        image: newCarImage || existingCar.image || '/Website-Images/Cars/ertiga.jpg',
        downPayment: newCarDownPayment ? `₹${newCarDownPayment.replace(/[^0-9]/g, '')}` : existingCar.downPayment,
        monthlyEmi: newCarEmi ? `₹${newCarEmi.replace(/[^0-9]/g, '')}/month` : existingCar.monthlyEmi,
        exShowroomPrice: newCarExShowroom ? parseInt(newCarExShowroom.replace(/[^0-9]/g, ''), 10) : existingCar.exShowroomPrice,
        variants: updatedVariants
      };
      updatedCars = [...currentCars];
    } else {
      const slug = fullCarName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const newCarItem: CarItem = {
        id: slug + '-' + Date.now(),
        name: fullCarName,
        carType: quotationCarType,
        image: newCarImage || '/Website-Images/Cars/ertiga.jpg',
        downPayment: newCarDownPayment ? `₹${newCarDownPayment.replace(/[^0-9]/g, '')}` : undefined,
        monthlyEmi: newCarEmi ? `₹${newCarEmi.replace(/[^0-9]/g, '')}/month` : undefined,
        exShowroomPrice: newCarExShowroom ? parseInt(newCarExShowroom.replace(/[^0-9]/g, ''), 10) : undefined,
        variants: [
          {
            name: finalVariant,
            colors: colorsArray.length > 0 ? colorsArray : ['White']
          }
        ]
      };
      updatedCars = [...currentCars, newCarItem];
    }

    saveCarInventory(updatedCars);
    setCars(updatedCars);
    setIsCarModalOpen(false);
    alert(`Car "${fullCarName}" with variant "${finalVariant}" saved successfully!`);
  };

  // Add New Variant to existing Car
  const handleSaveVariant = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCarForVariant) return;
    if (!variantName.trim()) {
      alert('Please enter variant name');
      return;
    }

    const currentCars = getCarInventory();
    const colorsArray = variantColors.split(',').map(c => c.trim()).filter(Boolean);

    const updatedCars = currentCars.map(car => {
      if (car.name === selectedCarForVariant.name || car.id === selectedCarForVariant.id) {
        const existingVariants = car.variants || [];
        // Check duplicate
        if (existingVariants.some(v => v.name.toLowerCase() === variantName.trim().toLowerCase())) {
          alert('This variant already exists for this car');
          return car;
        }
        return {
          ...car,
          variants: [
            ...existingVariants,
            {
              name: variantName.trim(),
              colors: colorsArray.length > 0 ? colorsArray : ['White']
            }
          ]
        };
      }
      return car;
    });

    saveCarInventory(updatedCars);
    setCars(updatedCars);
    setIsVariantModalOpen(false);
    alert(`Variant "${variantName}" added to ${selectedCarForVariant.name}! Available in Create Quotation now.`);
  };

  // Delete Car
  const handleDeleteCar = async (carId: string, carName: string) => {
    if (window.confirm(`Are you sure you want to permanently delete car "${carName}"?`)) {
      const updated = await deleteCarPermanently(carId, carName);
      setCars(updated);
    }
  };

  // Delete Variant from Car
  const handleDeleteVariant = async (carName: string, variantName: string) => {
    if (window.confirm(`Delete variant "${variantName}" from ${carName}?`)) {
      const updated = await deleteVariantPermanently(carName, variantName);
      setCars(updated);
    }
  };

  return (
    <div className="cars-management-page">
      
      {/* Header Bar */}
      <div className="module-header-bar">
        <div className="module-header-info">
          <div className="module-header-icon icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8C2.1 11 2 11.5 2 12v4c0 .6.4 1 1 1h2"></path>
              <circle cx="7" cy="17" r="2"></circle>
              <path d="M9 17h6"></path>
              <circle cx="17" cy="17" r="2"></circle>
            </svg>
          </div>
          <div>
            <h2 className="module-header-title">Car & Variant Inventory Management</h2>
            <p className="module-header-subtitle">Add new car models or variants. Changes automatically synchronize with Create Quotation.</p>
          </div>
        </div>
        <button 
          onClick={handleOpenAddCarModal}
          className="module-action-btn"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span>Add New Car</span>
        </button>
      </div>

      {/* Car Cards Grid */}
      <div className="cars-grid">
        {cars.map((car) => (
          <div key={car.id} className="car-inventory-card">
            
            {/* Header / Thumbnail Box */}
            <div className="car-card-header">
              <div className="car-thumb-wrap">
                <img 
                  src={car.image || '/Website-Images/Cars/ertiga.jpg'} 
                  alt={car.name} 
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/Website-Images/Cars/ertiga.jpg'; }}
                />
              </div>
              <div className="car-card-title-wrap">
                <h3>{car.name}</h3>
                <span className="variant-count-pill">{car.variants?.length || 0} Variant(s) Registered</span>
              </div>
              <button 
                onClick={() => handleDeleteCar(car.id, car.name)}
                className="car-del-btn"
                title="Delete Car Model"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>

            {/* Variants List Body */}
            <div className="car-card-body">
              <div>
                <div className="variant-section-head">
                  <h4>Registered Variants</h4>
                  <button 
                    onClick={() => handleOpenAddVariantModal(car)}
                    className="variant-add-btn"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    <span>Add Variant</span>
                  </button>
                </div>

                <div className="variant-chips-container">
                  {car.variants && car.variants.length > 0 ? (
                    car.variants.map((v, idx) => (
                      <div key={idx} className="variant-item-pill">
                        <div>
                          <div className="variant-name-text">{v.name}</div>
                          {v.colors && v.colors.length > 0 && (
                            <div className="variant-color-sub">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <circle cx="12" cy="8" r="2"></circle>
                                <circle cx="8" cy="14" r="2"></circle>
                                <circle cx="16" cy="14" r="2"></circle>
                              </svg>
                              <span>Colors: {v.colors.join(', ')}</span>
                            </div>
                          )}
                        </div>
                        {car.variants.length > 1 && (
                          <button 
                            onClick={() => handleDeleteVariant(car.name, v.name)}
                            className="variant-del-btn"
                            title="Delete Variant"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic', padding: '6px' }}>No variants added yet.</div>
                  )}
                </div>
              </div>

            </div>

          </div>
        ))}
      </div>

      {/* Add New Car Modal */}
      {isCarModalOpen && (
        <div className="dashboard-modal-backdrop">
          <div className="dashboard-modal-box" style={{ maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            
            <div className="modal-head">
              <div className="modal-head-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <h3>Add New Car Model</h3>
              </div>
              <button onClick={() => setIsCarModalOpen(false)} className="modal-close-btn">×</button>
            </div>

            <form onSubmit={handleSaveCar}>
              
              {/* 1. Category Classification */}
              <div className="form-group-compact" style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', color: '#1e293b', marginBottom: '6px' }}>
                  1. Select Car Category
                </label>
                <div className="quotation-category-toggle-grid">
                  <button
                    type="button"
                    className={`category-card-btn ${quotationCarType === 't-permit' ? 'active tpermit' : ''}`}
                    onClick={() => setQuotationCarType('t-permit')}
                  >
                    <div className="cat-icon" style={{ fontSize: '1.25rem' }}>🚖</div>
                    <div className="cat-details">
                      <span className="cat-title">T-Permit (Commercial)</span>
                      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Commercial Taxi Registration</span>
                    </div>
                    {quotationCarType === 't-permit' && <span style={{ marginLeft: 'auto', color: '#2563eb', fontWeight: 800 }}>✓</span>}
                  </button>
                  <button
                    type="button"
                    className={`category-card-btn ${quotationCarType === 'private' ? 'active private' : ''}`}
                    onClick={() => setQuotationCarType('private')}
                  >
                    <div className="cat-icon" style={{ fontSize: '1.25rem' }}>🚗</div>
                    <div className="cat-details">
                      <span className="cat-title">Private (Personal)</span>
                      <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Individual Personal Car</span>
                    </div>
                    {quotationCarType === 'private' && <span style={{ marginLeft: 'auto', color: '#059669', fontWeight: 800 }}>✓</span>}
                  </button>
                </div>
              </div>

              {/* 2. Car Brand Name */}
              <div className="form-group-compact" style={{ marginBottom: '12px' }}>
                <div className="section-label-row">
                  <label style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e293b' }}>
                    2. Car Brand Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <button
                    type="button"
                    className="toggle-mode-btn"
                    onClick={() => {
                      setIsCustomBrand(!isCustomBrand);
                      if (!isCustomBrand) {
                        setSelectedBrand('');
                        setIsCustomModel(true);
                        setIsCustomVariant(true);
                      } else {
                        setCustomBrandName('');
                      }
                    }}
                  >
                    {isCustomBrand ? '← Choose Existing Brand' : '+ Add New Brand'}
                  </button>
                </div>

                {isCustomBrand ? (
                  <input
                    type="text"
                    required
                    placeholder="Enter brand name (e.g. Maruti Suzuki, Tata, Hyundai, Toyota, Mahindra)"
                    value={customBrandName}
                    onChange={(e) => setCustomBrandName(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1.5px solid #3b82f6', fontSize: '0.84rem', boxSizing: 'border-box' }}
                    autoFocus
                  />
                ) : (
                  <select
                    value={selectedBrand}
                    onChange={(e) => handleBrandSelect(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.84rem', boxSizing: 'border-box', background: '#fff' }}
                  >
                    <option value="">-- Select Car Brand --</option>
                    {structure.brands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* 3. Car Model Name */}
              {(selectedBrand || isCustomBrand) && (
                <div className="form-group-compact" style={{ marginBottom: '12px' }}>
                  <div className="section-label-row">
                    <label style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e293b' }}>
                      3. Car Model Name <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    {!isCustomBrand && (
                      <button
                        type="button"
                        className="toggle-mode-btn"
                        onClick={() => {
                          setIsCustomModel(!isCustomModel);
                          if (!isCustomModel) {
                            setSelectedModel('');
                            setIsCustomVariant(true);
                          } else {
                            setCustomModelName('');
                          }
                        }}
                      >
                        {isCustomModel ? '← Choose Existing Model' : '+ Add New Model'}
                      </button>
                    )}
                  </div>

                  {isCustomModel || isCustomBrand ? (
                    <input
                      type="text"
                      required
                      placeholder="Enter model name (e.g. Ertiga, Wagon-R, Nexon, Punch, Aura, Dzire)"
                      value={customModelName}
                      onChange={(e) => setCustomModelName(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1.5px solid #3b82f6', fontSize: '0.84rem', boxSizing: 'border-box' }}
                      autoFocus={isCustomModel}
                    />
                  ) : (
                    <select
                      value={selectedModel}
                      onChange={(e) => handleModelSelect(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.84rem', boxSizing: 'border-box', background: '#fff' }}
                    >
                      <option value="">-- Select Car Model --</option>
                      {(structure.modelsByBrand[selectedBrand] || []).map(model => (
                        <option key={model} value={model}>{model}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* 4. Model Variant */}
              {(selectedModel || isCustomModel || isCustomBrand) && (
                <div className="form-group-compact" style={{ marginBottom: '12px' }}>
                  <div className="section-label-row">
                    <label style={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e293b' }}>
                      4. Model Variant <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    {(!isCustomBrand && !isCustomModel && availableVariants.length > 0) && (
                      <button
                        type="button"
                        className="toggle-mode-btn"
                        onClick={() => {
                          setIsCustomVariant(!isCustomVariant);
                          if (!isCustomVariant) {
                            setSelectedVariant('');
                          } else {
                            setCustomVariantName('');
                          }
                        }}
                      >
                        {isCustomVariant ? '← Choose Existing Variant' : '+ Add New Variant'}
                      </button>
                    )}
                  </div>

                  {isCustomVariant || isCustomModel || isCustomBrand || availableVariants.length === 0 ? (
                    <input
                      type="text"
                      required
                      placeholder="Enter variant name (e.g. ZXI CNG, Tour M, Adventure+ MT)"
                      value={customVariantName}
                      onChange={(e) => setCustomVariantName(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1.5px solid #3b82f6', fontSize: '0.84rem', boxSizing: 'border-box' }}
                      autoFocus={isCustomVariant}
                    />
                  ) : (
                    <select
                      value={selectedVariant}
                      onChange={(e) => {
                        setSelectedVariant(e.target.value);
                        setIsCustomVariant(false);
                      }}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.84rem', boxSizing: 'border-box', background: '#fff' }}
                    >
                      <option value="">-- Select Existing Variant --</option>
                      {availableVariants.map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* 5. Colors */}
              <div className="form-group-compact" style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', color: '#1e293b', marginBottom: '4px' }}>
                  Available Colors (Comma Separated)
                </label>
                <input 
                  type="text"
                  value={newCarColors}
                  onChange={(e) => setNewCarColors(e.target.value)}
                  placeholder="White, Silver, Grey, Red, Blue"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.84rem', boxSizing: 'border-box' }}
                />
              </div>

              {/* 6. Image Upload Box */}
              <div className="dropzone-modal-box" style={{ marginBottom: '12px', background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <label style={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', color: '#334155', marginBottom: '6px' }}>
                  Car Image / Photo (Upload File or URL)
                </label>
                {newCarImage && (
                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img 
                      src={newCarImage} 
                      alt="Preview" 
                      style={{ width: '64px', height: '42px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #cbd5e1' }} 
                    />
                    <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 700 }}>✓ Image Selected</span>
                  </div>
                )}
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ marginBottom: '6px', fontSize: '0.78rem', display: 'block' }}
                />
                <input 
                  type="text"
                  value={newCarImage}
                  onChange={(e) => setNewCarImage(e.target.value)}
                  placeholder="Or Image URL: e.g. /Website-Images/Cars/ertiga.jpg"
                  style={{ width: '100%', padding: '6px 9px', borderRadius: '5px', border: '1px solid #cbd5e1', fontSize: '0.78rem', boxSizing: 'border-box' }}
                />
              </div>

              {/* 7. Optional Pricing Information */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.75rem', color: '#64748b', marginBottom: '2px' }}>
                    Down Payment (Optional)
                  </label>
                  <input 
                    type="text"
                    value={newCarDownPayment}
                    onChange={(e) => setNewCarDownPayment(e.target.value)}
                    placeholder="e.g. ₹99,000"
                    style={{ width: '100%', padding: '6px 8px', borderRadius: '5px', border: '1px solid #cbd5e1', fontSize: '0.8rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.75rem', color: '#64748b', marginBottom: '2px' }}>
                    Monthly EMI (Optional)
                  </label>
                  <input 
                    type="text"
                    value={newCarEmi}
                    onChange={(e) => setNewCarEmi(e.target.value)}
                    placeholder="e.g. ₹15,000/mo"
                    style={{ width: '100%', padding: '6px 8px', borderRadius: '5px', border: '1px solid #cbd5e1', fontSize: '0.8rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div className="modal-foot">
                <button 
                  type="button" 
                  onClick={() => setIsCarModalOpen(false)}
                  className="btn-modal-cancel"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn-modal-primary"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                    <polyline points="7 3 7 8 15 8"></polyline>
                  </svg>
                  <span>Save Car & Variant</span>
                </button>
              </div>

            </form>

          </div>
        </div>
      )}

      {/* Add New Variant Modal */}
      {isVariantModalOpen && selectedCarForVariant && (
        <div className="dashboard-modal-backdrop">
          <div className="dashboard-modal-box">
            
            <div className="modal-head">
              <div className="modal-head-title">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <h3>Add Variant to {selectedCarForVariant.name}</h3>
              </div>
              <button onClick={() => setIsVariantModalOpen(false)} className="modal-close-btn">×</button>
            </div>

            <form onSubmit={handleSaveVariant}>
              
              <div className="form-group-compact">
                <label>New Variant Name *</label>
                <input 
                  type="text"
                  required
                  value={variantName}
                  onChange={(e) => setVariantName(e.target.value)}
                  placeholder="e.g. ZXI+ CNG or Accomplished MT"
                />
              </div>

              <div className="form-group-compact">
                <label>Available Colors (Comma Separated)</label>
                <input 
                  type="text"
                  value={variantColors}
                  onChange={(e) => setVariantColors(e.target.value)}
                  placeholder="White, Silver, Grey, Red, Blue"
                />
              </div>

              <div className="modal-foot">
                <button 
                  type="button" 
                  onClick={() => setIsVariantModalOpen(false)}
                  className="btn-modal-cancel"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn-modal-primary"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                    <polyline points="7 3 7 8 15 8"></polyline>
                  </svg>
                  <span>Add Variant</span>
                </button>
              </div>

            </form>

          </div>
        </div>
      )}

    </div>
  );
};

export default CarsManagement;
